import {
  DatasetType,
  FormattedWidgetDataDataset,
  ParsedWidgetDataLabel,
  StatusData,
  TWidgetSettings,
  VisualizationConstraint,
  WidgetOrder
} from 'types/GlobalWidget'
import { TagStatusSeverity } from 'types/GlobalWidgetTag'
import {
  TableDataType,
  TableCell,
  TableStatusSeverity
} from 'components_new/molecules/Table'
import { pathKey } from 'components_new/molecules/Table/utils'

export const ORDER_BY_SEGMENT_BY = 'ORDER_BY_SEGMENT_BY'

/**
 * The paths of every row that shows its children: a row a user has
 * opened or closed keeps that, the others follow the widget's setting
 * for how many levels start expanded.
 */
export function getExpandedPathSet(
  labels: ParsedWidgetDataLabel[],
  expandedLevels: number,
  overrides: Map<string, boolean>
): Set<string> {
  const expandedPathSet = new Set<string>()

  labels.forEach((label) => {
    const path = label.path.map((p) => p.display_label)

    // each prefix is a parent row, at the depth of its own length
    path.forEach((_, i) => {
      const key = pathKey(path.slice(0, i + 1))

      if (overrides.get(key) ?? i + 1 <= expandedLevels) {
        expandedPathSet.add(key)
      }
    })
  })

  return expandedPathSet
}

/**
 * Map widget-specific DatasetType to generic Table molecule TableDataType
 */
export const mapDatasetTypeToTableDataType = (
  datasetType: DatasetType
): TableDataType => {
  switch (datasetType) {
  case DatasetType.ENUM:
    return TableDataType.ENUM
  case DatasetType.NUMBER:
    return TableDataType.NUMBER
  case DatasetType.STRING:
    return TableDataType.STRING
  case DatasetType.NUMBER_TAG:
    return TableDataType.NUMBER_TAG
  case DatasetType.DATE:
    return TableDataType.DATE
  default:
    return TableDataType.STRING
  }
}

/**
 * Map widget StatusData to Table molecule status format
 */
export const mapStatusDataToTableStatus = (
  statusData: StatusData
): TableCell['status'] => {
  if (!statusData) return null

  return statusData.map((severity) => {
    switch (severity) {
    case TagStatusSeverity.ERROR:
      return TableStatusSeverity.ERROR
    case TagStatusSeverity.WARNING:
      return TableStatusSeverity.WARNING
    case TagStatusSeverity.SUCCESS:
      return TableStatusSeverity.SUCCESS
    }
  })
}

/**
 * Get current order (asc/desc).
 * Prioritizes manual order from click,
 * else use default from widget settings.
 */
export function getActiveOrder(
  manualOrder: WidgetOrder,
  settings: TWidgetSettings
): 'asc' | 'desc' {
  if (manualOrder.id === null) {
    const constraint = settings.sort_by.visualization_constraint

    // KPI_VARIABLES rows follow the configured account-level order
    // top-down, so the arrow shows ascending like SMALLEST
    return constraint === VisualizationConstraint.SMALLEST ||
      constraint === VisualizationConstraint.KPI_VARIABLES
      ? 'asc'
      : 'desc'
  }

  return manualOrder.dir
}

/**
 * The direction each label's backend index runs in relative to the
 * segment order, or null when the index says nothing about it. The
 * index holds the configured segment order (account levels, A-Ö,
 * chronological) when the widget's own sort sits on the segment, and
 * runs against the labels when the constraint is LARGEST.
 */
export function getSegmentIndexOrder(
  settings: TWidgetSettings
): 'asc' | 'desc' | null {
  if (settings.sort_by.selected !== true) {
    return null
  }

  return settings.sort_by.visualization_constraint ===
    VisualizationConstraint.LARGEST
    ? 'desc'
    : 'asc'
}

/**
 * Get current order by.
 * Prioritizes manual order by from click,
 * else use default from widget settings.
 */
export function getActiveOrderBy(
  manualOrder: WidgetOrder,
  settings: TWidgetSettings,
  datasets: FormattedWidgetDataDataset[]
): { id: string | null; index: number | null } {
  if (manualOrder.id !== null) {
    return manualOrder
  }

  if (settings.sort_by.selected === true) {
    return {
      id: ORDER_BY_SEGMENT_BY,
      index: null
    }
  }

  const orderDataset = datasets.find(
    (item) => settings.sort_by.selected === item.id
  )

  if (orderDataset) {
    return {
      id: orderDataset.id,
      index: orderDataset.index
    }
  }

  return {
    id: null,
    index: null
  }
}
